import {
  ChapterPracticeResponse,
  DailyPunchCardResponse,
  ExamResultResponse,
  KnowledgePracticeResponse,
  ListExamResultResponse,
} from '~/src/api/modules/exam/types';
import { ExamResultPageStatus, ExamResultStatus } from './types';
import {
  fetchChapterPracticeResult,
  fetchDailyPunchCardResult,
  fetchExamResult,
  fetchKnowledgePracticeResult,
  fetchListExamResult,
} from '~/src/api/modules/exam';
import { fetchUserWrongReport } from '@/src/api/modules/user-center/wrong/index';
import { fetchUserCollectReport } from '@/src/api/modules/user-center/collection/index';
import { UserWrongReportResponse } from '@/src/api/modules/user-center/wrong/types';
import { CustomFetchContext } from '~/src/api/requests/types';
import dayjs from 'dayjs';
import { QuestAnswerStatus } from '~/src/constants/exam';
import { getQuestionType } from '../exam-answer/utils';
import { isSubjective } from '~/src/utils/exam';

// 计算是否展示主观题状态
export const checkSubjective = (questType: number, answerType: number, isRight: number) => {
  return isSubjective(getQuestionType(questType, answerType)) && isRight !== QuestAnswerStatus.Undo;
};

export const checkSubjectiveByAnswer = (questType: number, answerType: number, answer: any) => {
  return isSubjective(getQuestionType(+questType, +answerType)) && answer && answer[0];
};

// 章节练习
export const updateChapterPracticeData = async (
  submitId: string,
  id: string,
  fetchContext: CustomFetchContext
): Promise<ExamResultPageStatus> => {
  const res = await fetchChapterPracticeResult(
    {
      submitId,
      id,
    },
    {},
    fetchContext
  );

  if (res && res.data) {
    return computeChapterPracticePageStatus(res.data);
  }

  return {} as ExamResultPageStatus;
};

const computeChapterPracticePageStatus = (rawData: ChapterPracticeResponse) => {
  const meta = {
    title: `答题报告-第${rawData.chapterSectionNum.chapterNum}章 第${rawData.chapterSectionNum.sectionNum}节 ${rawData.examName}`,
    status: rawData.rightRent > 60 ? ExamResultStatus.Success : ExamResultStatus.Failed,
    isFinished: rawData.isFinish === 1,
    sectionId: rawData.sectionId,
    chapterId: rawData.chapterId,
    submitId: rawData.submitId,
  } as ExamResultPageStatus['meta'];

  const dashboard = {
    total: +rawData.allNum,
    done: +rawData.alreadyNum,
    success: rawData.rightNum,
    failed: +rawData.wrongNum,
    undo: +rawData.allNum - +rawData.alreadyNum,
    accuracy: rawData.rightRent,
    duration: rawData.examTime,
    progress: +rawData.progressRent,
  } as ExamResultPageStatus['dashboard'];

  const questList = rawData.question.map((item, index) => {
    const showSubjective = checkSubjective(+item.type, +item.answerType, +item.isRight);

    return {
      id: +item.questionId,
      serialNumber: index,
      status: (showSubjective && QuestAnswerStatus.QuestSubjective) || +item.isRight,
    };
  }) as ExamResultPageStatus['questList'];

  return {
    meta,
    dashboard,
    questList,
  } as ExamResultPageStatus;
};

// 每日打卡
export const updateDailyPunchData = async (
  fetchContext: CustomFetchContext,
  pageStatus: ExamResultPageStatus
): Promise<ExamResultPageStatus> => {
  const res = await fetchDailyPunchCardResult(
    {
      date: pageStatus.meta.date || dayjs().format('YYYY-MM-DD'),
      id: pageStatus.meta.id,
      userSubject: pageStatus.meta.userSubject,
    },
    {},
    fetchContext
  );

  if (res && res.data) {
    return computeDailyPunchPageStatus(res.data);
  }

  return {} as ExamResultPageStatus;
};

const computeDailyPunchPageStatus = (rawData: DailyPunchCardResponse) => {
  const meta = {
    title: '每日打卡-答题报告',
    punchCardTime: rawData.submitTime,
    status: rawData.rightPercent > 60 ? ExamResultStatus.Success : ExamResultStatus.Failed,
    sectionName: rawData.sectionName,
  };

  const rightNum = (rawData.data || []).filter(item => item.result === 1).length;
  const wrongNum = (rawData.data || []).filter(item => item.result === 2).length;

  const dashboard = {
    total: rawData.todayTotalQuestion,
    done: rightNum + wrongNum,
    success: rightNum,
    accuracy: rawData.rightPercent,
    duration: rawData.examTime,
    submitTotalQuestion: rawData.submitTotalQuestion,
    todayTotalQuestion: rawData.todayTotalQuestion,
    totalSignCount: rawData.totalSignCount,
    signTop: rawData.signTop === false ? -1 : rawData.signTop,
  };

  const questList = (rawData.data || []).map((item, index) => {
    const showSubjective = checkSubjectiveByAnswer(
      +item.questionType,
      +item.answerType,
      item.userAnswer
    );

    return {
      id: +item.questionId,
      serialNumber: index,
      status: (showSubjective && QuestAnswerStatus.QuestSubjective) || item.result,
    };
  });

  return {
    meta,
    dashboard,
    questList,
  } as ExamResultPageStatus;
};

// 普通考试
export const updateExamResultData = async (
  answerId: string,
  number: string,
  fetchContext: CustomFetchContext
): Promise<ExamResultPageStatus> => {
  const res = await fetchExamResult(
    {
      id: answerId,
      number: number,
    },
    {},
    fetchContext
  );

  if (res && res.data) {
    return computeExamResultPageStatus(res.data);
  }

  return {} as ExamResultPageStatus;
};

const computeExamResultPageStatus = (rawData: ExamResultResponse) => {
  const meta = {
    title: rawData.title + '-答题报告',
    punchCardTime: rawData.submitTime,
    status: rawData.pass ? ExamResultStatus.Success : ExamResultStatus.Failed,
    examPaperId: rawData.examPaperId,
    version:
      +rawData.versionYear !== 0 && +rawData.versionId !== 0
        ? `${rawData.versionYear}-${rawData.versionId}`
        : '',
  };

  const dashboard = {
    total: rawData.total,
    done: rawData.alreadyNum,
    success: rawData.rightNum,
    failed: rawData.wrongNum,
    accuracy: rawData.rightPercent,
    duration: rawData.lenTime,
    pcScore: rawData.pcScore,
  };

  const questList = (rawData.data || []).map((item, index) => {
    const showSubjective = checkSubjectiveByAnswer(
      +item.questionType,
      +item.answerType,
      item.userAnswer
    );

    return {
      id: +item.questionId,
      serialNumber: index,
      status: (showSubjective && QuestAnswerStatus.QuestSubjective) || item.result,
    };
  });

  return {
    meta,
    dashboard,
    questList,
  } as unknown as ExamResultPageStatus;
};

// 模拟考场 历年试题
export const updateListExamResultData = async (
  id: string,
  type: string,
  fetchContext: CustomFetchContext
): Promise<ExamResultPageStatus> => {
  const res = await fetchListExamResult(
    {
      id: id,
      number: type,
    },
    {},
    fetchContext
  );

  if (res && res.data) {
    return computeListExamResultPageStatus(res.data);
  }

  return {} as ExamResultPageStatus;
};

const computeListExamResultPageStatus = (rawData: ListExamResultResponse) => {
  const meta = {
    title: rawData.title + '-答题报告',
    punchCardTime: rawData.submitTime,
    status: rawData.pass ? ExamResultStatus.Success : ExamResultStatus.Failed,
    examPaperId: rawData.examPaperId,
  };

  const dashboard = {
    total: rawData.total,
    done: rawData.alreadyNum,
    success: rawData.rightNum,
    failed: rawData.wrongNum,
    accuracy: rawData.rightPercent,
    duration: rawData.lenTime,
    pcScore: rawData.pcScore,
  };

  const questList = (rawData.data || []).map((item, index) => {
    const showSubjective = checkSubjectiveByAnswer(
      +item.questionType,
      +item.answerType,
      item.userAnswer
    );

    return {
      id: +item.questionId,
      serialNumber: index,
      status: showSubjective
        ? QuestAnswerStatus.QuestSubjective
        : (item.result as QuestAnswerStatus),
    };
  });

  return {
    meta,
    dashboard,
    questList,
  } as unknown as ExamResultPageStatus;
};

// 错题本
export const updateWrongQuestionResult = async (
  userSubject: string,
  fetchContext: CustomFetchContext
) => {
  const res = await fetchUserWrongReport({ userSubject: userSubject }, fetchContext);

  if (res && res.data) {
    return computeWrongQuestionResultPageStatus(res.data);
  }

  return {} as ExamResultPageStatus;
};

const computeWrongQuestionResultPageStatus = (rawData: UserWrongReportResponse) => {
  const meta = {
    title: '错题本-答题报告',
    punchCardTime: rawData.submitTime,
    status: rawData.rightPercent > 60 ? ExamResultStatus.Success : ExamResultStatus.Failed,
  };

  const dashboard = {
    total: rawData.allNum,
    done: rawData.alreadyNum,
    success: rawData.rightTotal,
    failed: +rawData.alreadyNum - +rawData.rightTotal,
    accuracy: rawData.rightPercent,
    duration: rawData.examTime,
  };

  const questList = (rawData.lists || []).map((item, index) => {
    const showSubjective = checkSubjectiveByAnswer(
      +item.questionType,
      +item.answerType,
      +item.userAnswer
    );

    return {
      id: +item.questionId,
      serialNumber: index,
      status: showSubjective
        ? QuestAnswerStatus.QuestSubjective
        : (item.result as QuestAnswerStatus),
    };
  });

  return {
    meta,
    dashboard,
    questList,
  } as unknown as ExamResultPageStatus;
};

// 收藏夹
export const updateCollectionResult = async (
  userSubject: string,
  fetchContext: CustomFetchContext
) => {
  const res = await fetchUserCollectReport({ userSubject: userSubject }, fetchContext);

  if (res && res.data) {
    return computeCollectionResultPageStatus(res.data);
  }

  return {} as ExamResultPageStatus;
};

const computeCollectionResultPageStatus = (rawData: UserWrongReportResponse) => {
  const meta = {
    title: '收藏夹-答题报告',
    punchCardTime: rawData.submitTime,
    status: rawData.rightPercent > 60 ? ExamResultStatus.Success : ExamResultStatus.Failed,
  };

  const dashboard = {
    total: rawData.allNum,
    done: rawData.alreadyNum,
    success: rawData.rightTotal,
    failed: +rawData.alreadyNum - +rawData.rightTotal,
    accuracy: rawData.rightPercent,
    duration: rawData.examTime,
  };

  const questList = (rawData.lists || []).map((item, index) => {
    const showSubjective = checkSubjectiveByAnswer(
      +item.questionType,
      +item.answerType,
      item.userAnswer
    );

    return {
      id: +item.questionId,
      serialNumber: index,
      status: showSubjective
        ? QuestAnswerStatus.QuestSubjective
        : (item.result as QuestAnswerStatus),
    };
  });

  return {
    meta,
    dashboard,
    questList,
  } as unknown as ExamResultPageStatus;
};

// 知识点练习
export const updateKnowledgePracticeResult = async (
  submitId: string,
  id: string,
  fetchContext: CustomFetchContext
): Promise<ExamResultPageStatus> => {
  const res = await fetchKnowledgePracticeResult(
    {
      submitId,
      id,
    },
    {},
    fetchContext
  );

  if (res && res.data) {
    return computeKnowledgePracticeResultPageStatus(res.data);
  }

  return {} as ExamResultPageStatus;
};

const computeKnowledgePracticeResultPageStatus = (rawData: KnowledgePracticeResponse) => {
  const meta = {
    title: `答题报告-${rawData.examName}`,
    status: rawData.rightRent > 60 ? ExamResultStatus.Success : ExamResultStatus.Failed,
    isFinished: rawData.isFinish === 1,
    sectionId: rawData.sectionId,
    chapterId: rawData.chapterId,
    submitId: rawData.submitId,
  } as ExamResultPageStatus['meta'];

  const dashboard = {
    total: +rawData.allNum,
    done: +rawData.alreadyNum,
    success: rawData.rightNum,
    failed: +rawData.wrongNum,
    undo: +rawData.allNum - +rawData.answerNum,
    accuracy: rawData.rightRent,
    duration: rawData.examTime,
    progress: +rawData.progressRent,
  } as ExamResultPageStatus['dashboard'];

  const questList = rawData.question.map((item, index) => {
    const showSubjective = checkSubjective(+item.type, +item.answerType, +item.isRight);

    return {
      id: +item.questionId,
      serialNumber: index,
      status: (showSubjective && QuestAnswerStatus.QuestSubjective) || +item.isRight,
    };
  }) as ExamResultPageStatus['questList'];

  return {
    meta,
    dashboard,
    questList,
  } as ExamResultPageStatus;
};
